/**
 * Cookie-consent helpers for the web app.
 *
 * The consent *decision* is stored in a first-party cookie scoped to `.latolabs.io` so it is shared
 * across landing (latolabs.io) and app (app.latolabs.io) — localStorage is per-origin and would not
 * cross subdomains. That cookie is "strictly necessary" (it records the choice) and so is itself
 * exempt from consent. Region comes from our Vercel edge function; the audit log goes to our own
 * backend. There is no third-party CMP. Nothing durable is written for analytics until the visitor
 * opts in (PostHog runs in `memory` mode until then; see ConsentManager).
 */

import { create } from "zustand";

export type ConsentChoice = "granted" | "denied";

/** Bump when the banner copy or the categories change — recorded with each consent event so a
 *  record ties to exactly what the user saw. */
const BANNER_VERSION = "2026-07-11";

const COOKIE_NAME = "lato_cookie_consent";
const VISITOR_KEY = "lato_visitor_id";
const REGION_KEY = "lato_consent_region";
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;
const BASE = (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? "";
const REGION_URL = import.meta.env.DEV
  ? `${BASE}/consent/region`
  : "https://www.latolabs.io/api/consent/region";

/** Share the decision across *.latolabs.io in prod; host-only on localhost / Vercel previews. */
function cookieDomainAttr(): string {
  return window.location.hostname.endsWith("latolabs.io") ? "; Domain=.latolabs.io" : "";
}

export function getConsent(): ConsentChoice | null {
  const match = document.cookie.match(/(?:^|;\s*)lato_cookie_consent=([^;]+)/);
  const value = match?.[1];
  return value === "granted" || value === "denied" ? value : null;
}

export function setConsent(choice: ConsentChoice): void {
  document.cookie = `${COOKIE_NAME}=${choice}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${cookieDomainAttr()}`;
}

/** A stable browser id for correlating consent records — only minted/stored once we're allowed to
 *  persist (i.e. on grant), so an undecided EU visitor leaves no localStorage behind. */
function visitorId(persist: boolean): string | undefined {
  try {
    let id = localStorage.getItem(VISITOR_KEY) ?? undefined;
    if (!id && persist) {
      id = crypto.randomUUID();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

/** Ask the region endpoint whether this visitor needs a consent banner. Fail-safe to "eu" (show it).
 *  Only the banner-showing "eu" verdict is cached in sessionStorage (deterministic per IP) to skip
 *  re-fetching within a session; "row" is never cached — a stale cached "row" could imply-grant
 *  analytics with no banner if the consent cookie is later lost, and it saves little since a "row"
 *  visitor gets a durable `granted` cookie after the first page. */
export async function fetchRegion(): Promise<"eu" | "row"> {
  try {
    if (sessionStorage.getItem(REGION_KEY) === "eu") return "eu";
  } catch {
    // sessionStorage unavailable — fall through to the network fetch
  }
  let region: "eu" | "row";
  try {
    // Vercel supplies a trusted edge-country header to the landing function. Railway does not,
    // and its optional MaxMind database is not part of the production image.
    const res = await fetch(REGION_URL, { credentials: "omit" });
    // Only a definitive OK answer is trusted; a network error or non-2xx (5xx/4xx) is a transient
    // failure that fails safe to "eu" without sticking for the session.
    if (!res.ok) return "eu";
    const data = (await res.json()) as { region?: string };
    region = data.region === "row" ? "row" : "eu";
  } catch {
    return "eu";
  }
  try {
    if (region === "eu") sessionStorage.setItem(REGION_KEY, region);
  } catch {
    // best-effort cache
  }
  return region;
}

/** Record a consent choice to our own backend (first-party audit). Best-effort — never blocks UX. */
export async function logConsent(choice: ConsentChoice): Promise<void> {
  try {
    const vid = visitorId(choice === "granted");
    await fetch(`${BASE}/consent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(vid ? { "X-Visitor-Id": vid } : {}),
      },
      body: JSON.stringify({ choice, banner_version: BANNER_VERSION, source: "web" }),
      credentials: "omit",
      keepalive: true,
    });
  } catch {
    // best-effort: analytics consent must never fail on a logging problem
  }
}

/** Reactive consent state shared across the tree: `open` lets any "Cookie preferences" affordance
 *  re-open the banner to withdraw; `choice` mirrors the applied decision so effects that must wait
 *  for opt-in (e.g. usePostHogIdentify) re-run when it flips — the cookie alone isn't reactive. */
export const useConsentPrefs = create<{
  choice: ConsentChoice | null;
  open: boolean;
  setChoice: (choice: ConsentChoice) => void;
  setOpen: (open: boolean) => void;
}>((set) => ({
  choice: null,
  open: false,
  setChoice: (choice) => set({ choice }),
  setOpen: (open) => set({ open }),
}));

export function openCookieSettings(): void {
  useConsentPrefs.getState().setOpen(true);
}
